<template>
  <div class="pts-question">
    <h5 class="pts-question__text">{{ question.text }}</h5>

    <!-- Radio question -->
    <div v-if="question.type === 'radio'">
      <b-form-group v-slot="{ ariaDescribedby }">
        <b-form-radio-group
          v-model="localAnswer"
          :options="question.options"
          :aria-describedby="ariaDescribedby"
          stacked
        />
      </b-form-group>
    </div>

    <!-- Yes / No question -->
    <div v-else-if="question.type === 'yesno'">
      <b-form-group v-slot="{ ariaDescribedby }">
        <b-form-radio-group
          v-model="localAnswer"
          :name="`pts-yesno-${question.id}`"
          :options="['Yes', 'No']"
          :aria-describedby="ariaDescribedby"
          stacked
        />
      </b-form-group>
    </div>

    <!-- Matrix question — each row has its own conditional reveal -->
    <div v-else-if="question.type === 'matrix'" class="pts-matrix">
      <div v-for="item in question.items" :key="item.id" class="pts-matrix__row">
        <div class="pts-matrix__item-label">{{ item.label }}</div>
        <b-form-group v-slot="{ ariaDescribedby }">
          <b-form-radio-group
            :checked="getMatrixItemAnswer(item.id)"
            :options="question.options"
            :aria-describedby="ariaDescribedby"
            stacked
            @input="onMatrixSelect(item, $event)"
          />
        </b-form-group>
        <div v-if="showMatrixItemReveal(item.id)" class="pts-reveal">
          <label class="pts-reveal__label">{{ question.reveal.label }}</label>
          <b-form-textarea
            :value="getMatrixItemDetail(item.id)"
            rows="2"
            placeholder="Add a few details…"
            @input="onMatrixDetail(item, $event)"
          />
        </div>
      </div>
    </div>

    <!-- Free text question -->
    <div v-else-if="question.type === 'text'" class="pts-text-question">
      <b-form-textarea
        :value="answer || ''"
        rows="4"
        :placeholder="question.optional ? 'Optional — add any comments here…' : 'Please share your thoughts…'"
        @input="$emit('answer', $event)"
      />
    </div>

    <!-- Conditional reveal for radio / yesno -->
    <div v-if="showReveal" class="pts-reveal">
      <label class="pts-reveal__label">{{ question.reveal.label }}</label>
      <b-form-textarea
        :value="reveal || ''"
        rows="3"
        placeholder="Add a few details…"
        @input="$emit('reveal', $event)"
      />
    </div>

    <!-- Follow-up asked on the same page for some answers (e.g. "How easy was it to use?") -->
    <div v-if="showFollowUp" class="pts-follow-up">
      <h6 class="pts-question__text pts-follow-up__text">{{ followUp.text }}</h6>
      <b-form-group v-slot="{ ariaDescribedby }">
        <b-form-radio-group
          :checked="followUpAnswer"
          :name="`pts-follow-up-${followUp.id}`"
          :options="followUp.type === 'yesno' ? ['Yes', 'No'] : followUp.options"
          :aria-describedby="ariaDescribedby"
          stacked
          @input="$emit('follow-up-answer', $event)"
        />
      </b-form-group>
      <div v-if="showFollowUpReveal" class="pts-reveal">
        <label class="pts-reveal__label">{{ followUp.reveal.label }}</label>
        <b-form-textarea
          :value="followUpReveal || ''"
          rows="3"
          placeholder="Add a few details…"
          @input="$emit('follow-up-reveal', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { revealShown, followUpOf, followUpShown } from '../survey-flow'

export default {
  props: {
    question: { type: Object, required: true },
    answer: { type: [String, Array, Object], default: null },
    reveal: { type: String, default: null },
    followUpAnswer: { type: String, default: null },
    followUpReveal: { type: String, default: null },
  },
  computed: {
    localAnswer: {
      get() { return this.answer },
      set(val) { this.$emit('answer', val) },
    },

    showReveal() {
      return revealShown(this.question, this.answer)
    },

    followUp() {
      return followUpOf(this.question)
    },

    showFollowUp() {
      return followUpShown(this.question, this.answer)
    },

    showFollowUpReveal() {
      return this.showFollowUp && revealShown(this.followUp, this.followUpAnswer)
    },
  },
  methods: {
    getMatrixItemAnswer(id) {
      const arr = Array.isArray(this.answer) ? this.answer : []
      return arr.find(a => a.id === id)?.answer || null
    },

    getMatrixItemDetail(id) {
      const arr = Array.isArray(this.answer) ? this.answer : []
      return arr.find(a => a.id === id)?.detail || ''
    },

    showMatrixItemReveal(id) {
      if (!this.question.reveal) return false
      return this.question.reveal.triggers.includes(this.getMatrixItemAnswer(id))
    },

    onMatrixSelect(item, value) {
      const arr = Array.isArray(this.answer) ? this.answer.map(a => ({ ...a })) : []
      const existing = arr.find(a => a.id === item.id)
      if (existing) {
        existing.answer = value
        if (this.question.reveal && !this.question.reveal.triggers.includes(value)) {
          existing.detail = ''
        }
      } else {
        arr.push({ id: item.id, question: item.label, answer: value, detail: '' })
      }
      this.$emit('answer', arr)
    },

    onMatrixDetail(item, value) {
      const arr = Array.isArray(this.answer) ? this.answer.map(a => ({ ...a })) : []
      const existing = arr.find(a => a.id === item.id)
      if (existing) {
        existing.detail = value
      } else {
        arr.push({ id: item.id, question: item.label, answer: null, detail: value })
      }
      this.$emit('answer', arr)
    },
  },
}
</script>
